import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * FinancialRefreshContext - Manages financial data refresh across the app
 * When loads, payments, or expenses are modified, this triggers refresh of financial data
 */
const FinancialRefreshContext = createContext();

export const useFinancialRefresh = () => {
  const context = useContext(FinancialRefreshContext);
  if (!context) {
    throw new Error('useFinancialRefresh must be used within FinancialRefreshProvider');
  }
  return context;
};

export const FinancialRefreshProvider = ({ children }) => {
  const [refreshTriggers, setRefreshTriggers] = useState({
    overview: 0,
    revenue: 0,
    costs: 0,
    payments: 0,
    all: 0
  });

  const [lastAction, setLastAction] = useState(null);

  // Trigger refresh for specific tab or all tabs
  const triggerRefresh = useCallback((tabs = ['all'], action = null) => {
    setRefreshTriggers(prev => {
      const updates = {};
      const tabsToUpdate = tabs.includes('all') ? ['overview', 'revenue', 'costs', 'payments', 'all'] : tabs;

      tabsToUpdate.forEach(tab => {
        updates[tab] = prev[tab] + 1;
      });

      return { ...prev, ...updates };
    });

    if (action) {
      setLastAction({
        ...action,
        timestamp: new Date().toISOString()
      });
    }
  }, []);

  // Helper methods for specific actions
  const onLoadCreated = useCallback((loadData) => {
    triggerRefresh(['overview', 'revenue'], {
      type: 'load_created',
      entity: 'load',
      data: loadData,
      message: `Load ${loadData.loadNumber} created - Revenue: $${loadData.revenue || 0}`
    });
  }, [triggerRefresh]);

  const onLoadUpdated = useCallback((loadData, previousStatus) => {
    const tabs = ['overview', 'revenue'];

    // If status changed to delivered, it impacts financials more
    if (loadData.status === 'delivered' && previousStatus !== 'delivered') {
      tabs.push('costs'); // Delivered loads may impact cost calculations
    }

    triggerRefresh(tabs, {
      type: 'load_updated',
      entity: 'load',
      data: loadData,
      message: `Load ${loadData.loadNumber} updated - Status: ${loadData.status}`
    });
  }, [triggerRefresh]);

  const onLoadDeleted = useCallback((loadData) => {
    triggerRefresh(['overview', 'revenue'], {
      type: 'load_deleted',
      entity: 'load',
      data: loadData,
      message: `Load ${loadData.loadNumber} deleted`
    });
  }, [triggerRefresh]);

  const onPaymentCreated = useCallback((paymentData) => {
    triggerRefresh(['overview', 'payments', 'costs'], {
      type: 'payment_created',
      entity: 'payment',
      data: paymentData,
      message: `Payment to ${paymentData.recipientName} created - Amount: $${paymentData.totalAmount || 0}`
    });
  }, [triggerRefresh]);

  const onPaymentUpdated = useCallback((paymentData) => {
    triggerRefresh(['overview', 'payments', 'costs'], {
      type: 'payment_updated',
      entity: 'payment',
      data: paymentData,
      message: `Payment ${paymentData.paymentNumber} updated`
    });
  }, [triggerRefresh]);

  const onPaymentDeleted = useCallback((paymentData) => {
    triggerRefresh(['overview', 'payments', 'costs'], {
      type: 'payment_deleted',
      entity: 'payment',
      data: paymentData,
      message: `Payment deleted`
    });
  }, [triggerRefresh]);

  const onExpenseCreated = useCallback((expenseData) => {
    triggerRefresh(['overview', 'costs'], {
      type: 'expense_created',
      entity: 'expense',
      data: expenseData,
      message: `Expense added - ${expenseData.category}: $${expenseData.amount || 0}`
    });
  }, [triggerRefresh]);

  const onExpenseUpdated = useCallback((expenseData) => {
    triggerRefresh(['overview', 'costs'], {
      type: 'expense_updated',
      entity: 'expense',
      data: expenseData,
      message: `Expense updated - ${expenseData.category}`
    });
  }, [triggerRefresh]);

  const onExpenseDeleted = useCallback((expenseData) => {
    triggerRefresh(['overview', 'costs'], {
      type: 'expense_deleted',
      entity: 'expense',
      data: expenseData,
      message: `Expense deleted`
    });
  }, [triggerRefresh]);

  const value = {
    refreshTriggers,
    lastAction,
    triggerRefresh,
    onLoadCreated,
    onLoadUpdated,
    onLoadDeleted,
    onPaymentCreated,
    onPaymentUpdated,
    onPaymentDeleted,
    onExpenseCreated,
    onExpenseUpdated,
    onExpenseDeleted
  };

  return (
    <FinancialRefreshContext.Provider value={value}>
      {children}
    </FinancialRefreshContext.Provider>
  );
};